import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions } from 'ag-charts-community';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import Holidays from 'date-holidays';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, permissionLevelEnum } from '../../../../constants';
import { usePeriodReportsQuery } from '../../../../hooks/queries/useReportsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useTheme } from '../../../../hooks/useTheme';
import { PeriodReport } from '../../../../services/apiSLTP/dailyReports/reports';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getColorByPercentage } from '../../../../utils/colors';
import { formatDate } from '../../../../utils/dates';
import GenericAgGridTable from '../../GenericAgGridTable';
const hd = new Holidays('FR');

interface PeriodReportsTableProps {
    startDate: Date;
    endDate: Date;
}

const PeriodReportsTable: React.FC<PeriodReportsTableProps> = ({ startDate, endDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme } = useTheme();

    const { data: dataPeriodReports, isLoading: isPeriodReportsLoading } = usePeriodReportsQuery({
        queries: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days = [];
        const current = new Date(startDate);
        current.setHours(0, 0, 0, 0);
        const last = new Date(endDate);
        last.setHours(0, 0, 0, 0);

        while (current <= last) {
            days.push({
                date: new Date(current),
                label: current.getDate().toString(),
            });
            current.setDate(current.getDate() + 1);
        }
        return days;
    }, [startDate, endDate]);

    const rowData = useMemo(() => {
        if (!dataPeriodReports) return [];

        return dataPeriodReports.sort((a, b) => {
            return formatName(a.user.firstName, a.user.lastName).localeCompare(formatName(b.user.firstName, b.user.lastName));
        });
    }, [dataPeriodReports, daysInMonth]);

    const itemStyler: (params: AgBarSeriesItemStylerParams<any>) => AgBarSeriesStyle = (params) => {
        const max = Math.max(...rowData.map((value) => value.dailyReportByDate.length));

        const color = getColorByPercentage(params.yValue ?? 0, max);
        return { fill: color };
    };

    const getSubmissionSparkline = (
        dailyReportByDate: { date: string; report: any }[],
        daysInMonth: { date: Date; label: string }[]
    ): { x: string; y: number; fill: string }[] => {
        const entries = dailyReportByDate.map(({ date, report }) => ({
            date: new Date(date),
            report,
        }));

        return daysInMonth.map(({ date }) => {
            const reportEntry = entries.find((entry) => isSameDay(entry.date, date));
            const submit = reportEntry?.report?.reportSubmitValidationTimeStamp ? new Date(reportEntry.report.reportSubmitValidationTimeStamp) : null;

            const deadline = new Date(date);
            deadline.setHours(17, 0, 0, 0);

            const y = submit ? (submit <= deadline ? 1 : -1) : 0;

            return {
                x: formatDate(date),
                y,
                fill: y === 1 ? muiTheme.palette.success.main : muiTheme.palette.error.main,
            };
        });
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef<PeriodReport>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                valueGetter: (params) => formatName(params.data?.user.firstName, params.data?.user.lastName),
                colId: 'name',
                pinned: 'left',
                initialWidth: 300,
                cellStyle: (params) => {
                    const status = params?.data?.user.crudStatus;
                    if (status === crudStatusEnum.DELETED) return { color: muiTheme.palette.error.main };
                    if (status === crudStatusEnum.INACTIVE) return { color: muiTheme.palette.action.disabled };
                    return undefined;
                },
            },

            /*{
                headerName: t('prodDays'),
                colId: 'totalparkline',
                cellRenderer: 'agSparklineCellRenderer',
                pinned: 'left',
                valueGetter: (params) => {
                    return [params.data?.dailyReportByDate.length];
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA - valueB;
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        direction: 'horizontal',
                        min: 0,
                        max: Math.max(...rowData.map((value) => value.dailyReportByDate.length)) + 5,

                        label: {
                            enabled: true,
                            placement: 'outside-end',
                            color: '#fff',
                        },

                        itemStyler: itemStyler,
                    } as AgSparklineOptions,
                },
            },*/
            {
                headerName: t('submissionQuality'),
                colId: 'submissionQuality',
                valueGetter: (params) => {
                    const values = getSubmissionSparkline(params.data?.dailyReportByDate || [], daysInMonth)
                        .map((v) => v.y)
                        .filter((v) => v !== 0);
                    const count = values.length;
                    if (count === 0) return null;

                    const score = values.reduce((sum, y) => sum + y, 0) / count;

                    return score;
                },
                cellRenderer: (params: ICellRendererParams<PeriodReport>) => {
                    const score = params.value;

                    if (score === null || score === undefined) return '—';

                    let color = 'gray';
                    let label = t('null');

                    if (score >= 1) {
                        color = '#27ae60'; // vert foncé
                        label = t('perfect');
                    } else if (score >= 0.8) {
                        color = '#6fcf97'; // vert clair
                        label = t('diligent');
                    } else if (score >= 0.5) {
                        color = '#56ccf2'; // bleu clair
                        label = t('consistent');
                    } else if (score >= 0.2) {
                        color = '#f2c94c'; // or
                        label = t('inconsistent');
                    } else if (score > 0) {
                        color = '#f2994a'; // orange
                        label = t('unreliable');
                    } else {
                        color = '#eb5757'; // rouge
                        label = t('null');
                    }

                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                    flexShrink: 0,
                                }}
                            />
                            <span>{label.charAt(0).toUpperCase() + label.slice(1)}</span>
                        </div>
                    );
                },
                initialWidth: 180,
            },
            {
                headerName: t('submissionHistory'),
                colId: 'submissionSparkline',
                cellRenderer: 'agSparklineCellRenderer',
                initialFlex: 1,
                minWidth: 400,
                valueGetter: (params) => {
                    return getSubmissionSparkline(params.data?.dailyReportByDate || [], daysInMonth);
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        xKey: 'x',
                        yKey: 'y',
                        min: -1.5,
                        max: 1.5,
                        tooltip: {
                            renderer: (params) => ({
                                content:
                                    params.yValue === 1
                                        ? `${params.xValue} : ✅ ${t('before17h')}`
                                        : params.yValue === -1
                                          ? `${params.xValue} : ❌ ${t('after17h')}`
                                          : `${params.xValue} : 🕓 ${t('absentOrMissing')}`,
                            }),
                        },
                        itemStyler: (params: AgBarSeriesItemStylerParams<any>) => ({
                            fill: params.yValue === 1 ? '#6fcf97' : '#eb5757',
                        }),
                    } as AgSparklineOptions,
                },
            },
        ];

        /*const dateCols: ColDef[] = daysInMonth.map(({ date }) => {
            const iso = date.toISOString().split('T')[0];

            return {
                headerName: format(date, 'dd/MM'),
                field: `day_${iso}`,
                colId: `day_${iso}`,
                initialWidth: 110,
                cellStyle: (params: CellClassParams<PeriodReport>) => {
                    const dayReport = (params.data?.dailyReportByDate || []).find((r) => isSameDay(new Date(r.date), date));

                    if (!dayReport?.report) return {backgroundColor: ''};
                    if (!dayReport?.report.reportSubmitValidationTimeStamp) return {backgroundColor: 'gray'};

                    const submitDate = new Date(dayReport.report.reportSubmitValidationTimeStamp || '');
                    const deadline = new Date(date);
                    deadline.setHours(17, 0, 0, 0);

                    return submitDate <= deadline ? {backgroundColor: 'green'} : {backgroundColor: 'crimson'};
                },
                tooltipValueGetter: (params: ITooltipParams<PeriodReport>) => {
                    const dayReport = (params.data?.dailyReportByDate || []).find((r) => isSameDay(new Date(r.date), date));

                    if (!dayReport?.report) return 'Aucun rapport';
                    if (!dayReport?.report.reportSubmitValidationTimeStamp) return 'Pas encore envoyé';
                    const submitDate = new Date(dayReport.report.reportSubmitValidationTimeStamp);
                    return `Soumis à ${format(submitDate, 'HH:mm')}`;
                },
                cellClass: 'calendar-cell',
                sortable: false,
                filter: false,
            };
        });*/

        const adminCols: ColDef<PeriodReport>[] =
            user?.profileId.permsLevel === permissionLevelEnum.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: 'user._id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...adminCols];
    }, [t, daysInMonth, user, rowData]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`reporting-reports-${formatDate(startDate)}-${formatDate(endDate)}`}
                agGridProps={{ getRowId: ({ data }) => data._id, rowHeight: 40 }}
                loading={isPeriodReportsLoading}
            />
        </>
    );
};

export default PeriodReportsTable;
